import type { GuideDefinition } from './types';

// Employer Setup Guide. Sections walk a new household employer from "am I even an
// employer?" through registration, procedure choice, insurance, rates, contract,
// deadlines, and documents. It deliberately covers the obligations *around* the job, not
// how to drive the app itself — a feature tour is a separate, later concern. Personalized
// blocks (Ausgleichskasse, rates, VAV eligibility, deadlines, documents) are injected by
// the page via `extra`.

export const employerGuide: GuideDefinition = {
  id: 'employer',
  sections: [
    {
      id: 'obligations',
      icon: 'fa-scale-balanced',
      titleKey: 'guide.employer.obligations.title',
      descKey: 'guide.employer.obligations.desc',
      steps: [
        {
          id: 'understand',
          icon: 'fa-circle-info',
          type: 'info',
          titleKey: 'guide.employer.obligations.understand.title',
          bodyKey: 'guide.employer.obligations.understand.body',
          bulletKeys: [
            'guide.employer.obligations.understand.b1',
            'guide.employer.obligations.understand.b2',
            'guide.employer.obligations.understand.b3',
          ],
          manualCheck: true,
        },
      ],
    },
    {
      id: 'register',
      icon: 'fa-id-card',
      titleKey: 'guide.employer.register.title',
      descKey: 'guide.employer.register.desc',
      extra: 'cantonInfo',
      steps: [
        {
          id: 'register',
          icon: 'fa-building-columns',
          type: 'external',
          titleKey: 'guide.employer.register.register.title',
          bodyKey: 'guide.employer.register.register.body',
          bulletKeys: [
            'guide.employer.register.register.b1',
            'guide.employer.register.register.b2',
          ],
          autoDeriveKey: 'registered',
          manualCheck: true,
          cta: {
            labelKey: 'guide.employer.register.register.cta',
            kind: 'external',
            hrefToken: 'registrationUrl',
          },
        },
      ],
    },
    {
      id: 'procedure',
      icon: 'fa-route',
      titleKey: 'guide.employer.procedure.title',
      descKey: 'guide.employer.procedure.desc',
      extra: 'vavEligibility',
      steps: [
        {
          id: 'choose',
          icon: 'fa-list-check',
          type: 'info',
          titleKey: 'guide.employer.procedure.choose.title',
          bodyKey: 'guide.employer.procedure.choose.body',
          bulletKeys: [
            'guide.employer.procedure.choose.b1',
            'guide.employer.procedure.choose.b2',
            'guide.employer.procedure.choose.b3',
          ],
          manualCheck: true,
        },
      ],
    },
    {
      id: 'insurance',
      icon: 'fa-shield-heart',
      titleKey: 'guide.employer.insurance.title',
      descKey: 'guide.employer.insurance.desc',
      steps: [
        {
          id: 'uvg',
          icon: 'fa-briefcase-medical',
          type: 'info',
          titleKey: 'guide.employer.insurance.uvg.title',
          bodyKey: 'guide.employer.insurance.uvg.body',
          bulletKeys: [
            'guide.employer.insurance.uvg.b1',
            'guide.employer.insurance.uvg.b2',
          ],
          manualCheck: true,
        },
        {
          id: 'bvg',
          icon: 'fa-piggy-bank',
          type: 'info',
          titleKey: 'guide.employer.insurance.bvg.title',
          bodyKey: 'guide.employer.insurance.bvg.body',
        },
        {
          id: 'fak',
          icon: 'fa-children',
          type: 'info',
          titleKey: 'guide.employer.insurance.fak.title',
          bodyKey: 'guide.employer.insurance.fak.body',
        },
      ],
    },
    {
      id: 'rates',
      icon: 'fa-percent',
      titleKey: 'guide.employer.rates.title',
      descKey: 'guide.employer.rates.desc',
      extra: 'rates',
      steps: [
        {
          id: 'overview',
          icon: 'fa-table',
          type: 'info',
          titleKey: 'guide.employer.rates.overview.title',
          bodyKey: 'guide.employer.rates.overview.body',
        },
      ],
    },
    {
      id: 'contract',
      icon: 'fa-file-signature',
      titleKey: 'guide.employer.contract.title',
      descKey: 'guide.employer.contract.desc',
      steps: [
        {
          id: 'written',
          icon: 'fa-file-lines',
          type: 'info',
          titleKey: 'guide.employer.contract.written.title',
          bodyKey: 'guide.employer.contract.written.body',
          bulletKeys: [
            'guide.employer.contract.written.b1',
            'guide.employer.contract.written.b2',
          ],
          manualCheck: true,
        },
      ],
    },
    {
      id: 'deadlines',
      icon: 'fa-calendar-check',
      titleKey: 'guide.employer.deadlines.title',
      descKey: 'guide.employer.deadlines.desc',
      extra: 'deadlines',
      steps: [
        {
          id: 'know',
          icon: 'fa-bell',
          type: 'info',
          titleKey: 'guide.employer.deadlines.know.title',
          bodyKey: 'guide.employer.deadlines.know.body',
          bulletKeys: [
            'guide.employer.deadlines.know.b1',
            'guide.employer.deadlines.know.b2',
            'guide.employer.deadlines.know.b3',
          ],
          manualCheck: true,
        },
      ],
    },
    {
      id: 'documents',
      icon: 'fa-folder-open',
      titleKey: 'guide.employer.documents.title',
      descKey: 'guide.employer.documents.desc',
      extra: 'documents',
      steps: [],
    },
    {
      id: 'done',
      icon: 'fa-check',
      titleKey: 'guide.employer.done.title',
      steps: [
        {
          id: 'done',
          icon: 'fa-flag-checkered',
          type: 'info',
          titleKey: 'guide.employer.done.done.title',
          bodyKey: 'guide.employer.done.done.body',
        },
      ],
    },
  ],
};
